import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { getCustomerDisplayName, getCustomerOptionLabel } from "../utils/customerDisplay";
import { RequestStats } from "../components/requests/RequestStats";
import { RequestsTable } from "../components/requests/RequestsTable";
import { CreateRequestModal } from "../components/requests/CreateRequestModal";
import { RequestDetailModal } from "../components/requests/RequestDetailModal";
import { ResolveRequestModal } from "../components/requests/ResolveRequestModal";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { FileText, Plus, Download } from "lucide-react";
import { downloadCSV, csvDate } from "../utils/csv";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  NUEVA: "Nueva",
  EN_TRATAMIENTO: "En Tratamiento",
  PENDIENTE_PROVEEDOR: "Pendiente Proveedor",
  PENDIENTE_CLIENTE: "Pendiente Cliente",
  RESUELTO: "Resuelto",
};

const statusColors = {
  NUEVA: "bg-blue-500",
  EN_TRATAMIENTO: "bg-cyan-500",
  PENDIENTE_PROVEEDOR: "bg-purple-500",
  PENDIENTE_CLIENTE: "bg-yellow-500",
  RESUELTO: "bg-green-500",
};

const priorityLabels = {
  BAJA: "Baja",
  MEDIA: "Media",
  ALTA: "Alta",
  CRITICA: "Crítica",
};

const priorityColors = {
  BAJA: "bg-slate-500",
  MEDIA: "bg-blue-500",
  ALTA: "bg-orange-500",
  CRITICA: "bg-red-600",
};

const RequestsPage = () => {
  const { user, hasPermission, canDelete, canExport } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [resolutionSummary, setResolutionSummary] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  
  const [newRequest, setNewRequest] = useState({
    customer_id: "",
    service_id: "",
    location_id: "",
    title: "",
    description: "",
    priority: "MEDIA",
    assigned_to_id: "",
  });

  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [supportUsers, setSupportUsers] = useState([]); // technicians available for assignment

  // Filter customers by search term (nombre, DNI, código)
  const filteredCustomers = customers.filter(c => {
    if (!customerSearch) return true;
    const search = customerSearch.toLowerCase();
    const display = getCustomerDisplayName(c).toLowerCase();
    const apellidos = (c.apellidos || "").toLowerCase();
    const dni = (c.dni_nie_cif || c.dni_nie || c.cif || "").toLowerCase();
    const codigo = (c.customer_id || "").toLowerCase();
    return display.includes(search) || apellidos.includes(search) ||
           dni.includes(search) || codigo.includes(search);
  });

  const fetchRequests = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (priorityFilter !== "all") params.priority = priorityFilter;
      
      const response = await axios.get(`${API_URL}/api/requests`, { params });
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/requests/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchSupportUsers = async () => {
    // Same strategy as Tickets Internos: prefer users with SOPORTE/TECNICO role,
    // fall back to all active users so the assign dropdown is never empty.
    try {
      const [tecRes, supRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/by-role/TECNICO`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/users/by-role/SOPORTE`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/users`).catch(() => ({ data: [] })),
      ]);
      const byId = new Map();
      [...(tecRes.data || []), ...(supRes.data || [])].forEach(u => { if (u?.id) byId.set(u.id, u); });
      let list = Array.from(byId.values());
      if (list.length === 0) {
        list = (allRes.data || []).filter(u => u.is_active !== false);
      }
      setSupportUsers(list);
    } catch (e) {
      console.error("Error fetching support users:", e);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchCustomerServices = async (customerId) => {
    try {
      const response = await axios.get(`${API_URL}/api/customer-services`, {
        params: { customer_id: customerId }
      });
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchStats();
    fetchCustomers();
    fetchSupportUsers();
  }, [fetchRequests]);

  // Handle URL params for auto-opening create modal
  useEffect(() => {
    const action = searchParams.get("action");
    const customerId = searchParams.get("customer");
    const serviceId = searchParams.get("service");

    if (action === "create") {
      if (customerId) {
        // Find customer to get display name
        const customer = customers.find(c => c.id === customerId);
        if (customer) {
          setCustomerSearch(getCustomerOptionLabel(customer));
          setNewRequest(prev => ({ ...prev, customer_id: customerId, service_id: serviceId || "" }));
        } else {
          setNewRequest(prev => ({ ...prev, customer_id: customerId, service_id: serviceId || "" }));
        }
      }
      setCreateModalOpen(true);
    }
  }, [searchParams, customers]);

  // Deep-link: open specific request detail when ?open=<id> is present
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || requests.length === 0 || detailModalOpen) return;
    const target = requests.find((r) => r.id === openId);
    if (target) {
      openDetailModal(target);
      navigate("/requests", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, requests]);

  useEffect(() => {
    if (newRequest.customer_id) {
      fetchCustomerServices(newRequest.customer_id);
    }
  }, [newRequest.customer_id]);

  const handleCreateRequest = async () => {
    if (!newRequest.customer_id || !newRequest.title || !newRequest.description) {
      toast.error("Cliente, título y descripción son obligatorios");
      return;
    }

    try {
      const payload = {
        ...newRequest,
        assigned_to_id: newRequest.assigned_to_id || null,
      };
      await axios.post(`${API_URL}/api/requests`, payload);
      toast.success("Solicitud creada correctamente");
      setCreateModalOpen(false);
      setNewRequest({
        customer_id: "",
        service_id: "",
        location_id: "",
        title: "",
        description: "",
        priority: "MEDIA",
        assigned_to_id: "",
      });
      setCustomerSearch("");
      fetchRequests();
      fetchStats();
    } catch (error) {
      console.error("Error creating request:", error);
      toast.error(error.response?.data?.detail || "Error al crear solicitud");
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedRequest) return;
    
    if (newStatus === "RESUELTO") {
      setResolveModalOpen(true);
      return;
    }

    try {
      const res = await axios.put(`${API_URL}/api/requests/${selectedRequest.id}`, {
        status: newStatus
      });
      const updatedRequest = res.data;
      toast.success("Estado actualizado");
      
      // Update local state immediately for real-time UI
      setSelectedRequest(updatedRequest);
      setRequests(prevRequests => 
        prevRequests.map(r => r.id === selectedRequest.id ? { ...r, ...updatedRequest } : r)
      );
      fetchStats();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar estado");
    }
  };

  const handleAssignTechnician = async (assigneeId) => {
    if (!selectedRequest) return;
    try {
      const res = await axios.put(`${API_URL}/api/requests/${selectedRequest.id}`, {
        assigned_to_id: assigneeId || null,
      });
      const updated = res.data;
      toast.success(assigneeId ? "Técnico asignado" : "Asignación eliminada");
      setSelectedRequest(updated);
      setRequests(prev => prev.map(r => r.id === selectedRequest.id ? { ...r, ...updated } : r));
    } catch (error) {
      console.error("Error assigning technician:", error);
      toast.error(error.response?.data?.detail || "Error al asignar técnico");
    }
  };

  const handleResolveRequest = async () => {
    if (!resolutionSummary.trim()) {
      toast.error("El resumen de la solución es obligatorio");
      return;
    }

    try {
      const res = await axios.put(`${API_URL}/api/requests/${selectedRequest.id}`, {
        status: "RESUELTO",
        resolution_summary: resolutionSummary
      });
      const updatedRequest = res.data;
      toast.success("Solicitud resuelta");
      setResolveModalOpen(false);
      setResolutionSummary("");
      
      // Update local state immediately for real-time UI
      setSelectedRequest(updatedRequest);
      setRequests(prevRequests => 
        prevRequests.map(r => r.id === selectedRequest.id ? { ...r, ...updatedRequest } : r)
      );
      fetchStats();
    } catch (error) {
      console.error("Error resolving request:", error);
      toast.error(error.response?.data?.detail || "Error al resolver solicitud");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedRequest) return;

    try {
      const res = await axios.post(`${API_URL}/api/requests/${selectedRequest.id}/comments`, {
        content: newComment
      });
      const updatedRequest = res.data;
      toast.success("Comentario añadido");
      setNewComment("");
      
      // Update local state immediately for real-time UI
      setSelectedRequest(updatedRequest);
      setRequests(prevRequests => 
        prevRequests.map(r => r.id === selectedRequest.id ? { ...r, ...updatedRequest } : r)
      );
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Error al añadir comentario");
    }
  };

  const handleDeleteRequest = async () => {
    if (!requestToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/requests/${requestToDelete.id}`);
      toast.success("Solicitud eliminada correctamente");
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
      setDetailModalOpen(false);
      fetchRequests();
      fetchStats();
    } catch (error) {
      console.error("Error deleting request:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar solicitud");
    }
  };

  const openDeleteDialog = (e, request) => {
    e.stopPropagation();
    setRequestToDelete(request);
    setDeleteDialogOpen(true);
  };

  const openDetailModal = async (request) => {
    try {
      const response = await axios.get(`${API_URL}/api/requests/${request.id}`);
      setSelectedRequest(response.data);
      setDetailModalOpen(true);
    } catch (error) {
      console.error("Error fetching request details:", error);
      toast.error("Error al cargar detalles");
    }
  };

  const filteredRequests = requests.filter(req => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        req.request_number?.toLowerCase().includes(search) ||
        req.title?.toLowerCase().includes(search) ||
        req.customer_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const canEdit = user?.roles?.some(r => ["TECNICO", "GESTION", "SUPERADMIN"].includes(r));

  const handleExportCSV = () => {
    if (filteredRequests.length === 0) {
      toast.error("No hay solicitudes para exportar");
      return;
    }
    const columns = [
      { key: "request_number", label: "Nº Solicitud" },
      { key: "title", label: "Título" },
      { key: "customer_name", label: "Cliente" },
      { key: "request_type", label: "Tipo" },
      { key: "status", label: "Estado", format: (v) => statusLabels[v] || v || "" },
      { key: "priority", label: "Prioridad", format: (v) => priorityLabels[v] || v || "" },
      { key: "assigned_to_name", label: "Asignado a" },
      { key: "created_at", label: "Creada", format: csvDate },
    ];
    downloadCSV("solicitudes", columns, filteredRequests);
    toast.success(`${filteredRequests.length} solicitudes exportadas a CSV`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="requests-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <FileText className="h-8 w-8 text-[#03BEE4]" />
            Solicitudes
          </h1>
          <p className="text-slate-400 mt-1">
            Gestión de solicitudes de clientes
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canExport("requests") && (
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="border-[#42E695] text-[#42E695] hover:bg-[#42E695]/20"
              data-testid="export-requests-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
          )}
          {hasPermission("requests", "create") && (
            <Button
              onClick={() => setCreateModalOpen(true)}
              className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]"
              data-testid="create-request-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nueva Solicitud
            </Button>
          )}
        </div>
      </div>

      <RequestStats
        stats={stats}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
      />

      <RequestsTable
        filteredRequests={filteredRequests}
        statusColors={statusColors}
        statusLabels={statusLabels}
        priorityColors={priorityColors}
        priorityLabels={priorityLabels}
        formatDate={formatDate}
        openDetailModal={openDetailModal}
        canDelete={canDelete}
        openDeleteDialog={openDeleteDialog}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        requestToDelete={requestToDelete}
        handleDeleteRequest={handleDeleteRequest}
      />

      <CreateRequestModal
        createModalOpen={createModalOpen}
        setCreateModalOpen={setCreateModalOpen}
        customerSearch={customerSearch}
        setCustomerSearch={setCustomerSearch}
        showCustomerDropdown={showCustomerDropdown}
        setShowCustomerDropdown={setShowCustomerDropdown}
        newRequest={newRequest}
        setNewRequest={setNewRequest}
        services={services}
        setServices={setServices}
        filteredCustomers={filteredCustomers}
        supportUsers={supportUsers}
        handleCreateRequest={handleCreateRequest}
      />

      <RequestDetailModal
        detailModalOpen={detailModalOpen}
        setDetailModalOpen={setDetailModalOpen}
        selectedRequest={selectedRequest}
        statusColors={statusColors}
        statusLabels={statusLabels}
        priorityColors={priorityColors}
        priorityLabels={priorityLabels}
        canEdit={canEdit}
        handleStatusChange={handleStatusChange}
        handleAssignTechnician={handleAssignTechnician}
        supportUsers={supportUsers}
        formatDate={formatDate}
        newComment={newComment}
        setNewComment={setNewComment}
        handleAddComment={handleAddComment}
      />

      <ResolveRequestModal
        resolveModalOpen={resolveModalOpen}
        setResolveModalOpen={setResolveModalOpen}
        resolutionSummary={resolutionSummary}
        setResolutionSummary={setResolutionSummary}
        handleResolveRequest={handleResolveRequest}
      />
    </div>
  );
};

export default RequestsPage;
